import { useEffect, useRef, useState } from "react";
import { api } from "@/api/client";
import type { Conversation } from "@/messaging/types";
import { BellOffIcon, EditIcon, MoreIcon, PinIcon, TrashIcon, UsersIcon } from "./icons";

export function ConversationMenu({
  conversation,
  myId,
  onPatch,
  onViewMembers,
  onRename,
  onLeft,
}: {
  conversation: Conversation;
  myId: string | null;
  onPatch: (id: string, partial: Partial<Conversation>) => void;
  onViewMembers: () => void;
  onRename: () => void;
  onLeft: () => void;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement | null>(null);
  const isGroup = conversation.kind === "group";
  const isAdmin = conversation.created_by === myId && !conversation.is_system;

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const patch = async (partial: Partial<Conversation>) => {
    onPatch(conversation.id, partial);
    setOpen(false);
    try {
      await api(`/api/conversations/${conversation.id}`, {
        method: "PATCH",
        json: partial,
      });
    } catch {
      /* WS/refresh reconciles */
    }
  };

  const leave = async () => {
    setOpen(false);
    try {
      await api(`/api/conversations/${conversation.id}/members/${myId}`, {
        method: "DELETE",
      });
      onLeft();
    } catch {
      /* ignore */
    }
  };

  const item =
    "flex w-full items-center gap-2.5 px-3 py-2 text-sm text-slate-200 hover:bg-white/[0.05] transition text-left";

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen((v) => !v)}
        className={`icon-btn h-9 w-9 ${open ? "icon-btn-active" : ""}`}
        title="Options"
      >
        <MoreIcon />
      </button>
      {open && (
        <div className="absolute right-0 mt-1 w-52 overflow-hidden rounded-xl border border-white/10 bg-ink-900 shadow-2xl z-[1200]">
          {isGroup && (
            <button className={item} onClick={() => { setOpen(false); onViewMembers(); }}>
              <UsersIcon size={16} /> Members
            </button>
          )}
          <button className={item} onClick={() => patch({ pinned: !conversation.pinned })}>
            <PinIcon size={16} /> {conversation.pinned ? "Unpin" : "Pin"}
          </button>
          <button className={item} onClick={() => patch({ muted: !conversation.muted })}>
            <BellOffIcon size={16} /> {conversation.muted ? "Unmute" : "Mute"}
          </button>
          {isAdmin && (
            <button className={item} onClick={() => { setOpen(false); onRename(); }}>
              <EditIcon size={16} /> Rename
            </button>
          )}
          {isGroup && !conversation.is_system && (
            <button className={`${item} !text-rose-300`} onClick={leave}>
              <TrashIcon size={16} /> Leave group
            </button>
          )}
        </div>
      )}
    </div>
  );
}
